import React from 'react';
import { Controller } from 'react-hook-form';
import EcxTimePicker from '../../../common/EcxTimePicker';
import { formatDisplayTime, normalizeTimeToInterval, normalizeTimeValue } from '../../../common/ecxTimeUtils';
import { ClinicInfoPanel } from '../clinicsettings/info/clinicInfoParts';

export const CALL_PREFERENCE_OPTIONS = [
    { value: 'DISABLED', label: 'Disabled' },
    { value: 'HOME_PHONE', label: 'Home Phone' },
    { value: 'CELL_PHONE', label: 'Cell Phone' },
];

export const APPT_SUMMARY_OPTIONS = [
    { value: 'ECP', label: 'ECP' },
    { value: 'CLINIC', label: 'Clinic' },
    { value: 'BOTH', label: 'Both' },
];

export const PREBOOK_REMINDER_DAY_OPTIONS = Array.from({ length: 10 }, (_, index) => {
    const days = 36 + index;
    return { value: String(days), label: `${days} Days` };
});

export const FIRST_REMINDER_DAY_OPTIONS = Array.from({ length: 8 }, (_, index) => {
    const days = 7 + index;
    return { value: String(days), label: `${days} Days` };
});

export const SECOND_REMINDER_DAY_OPTIONS = Array.from({ length: 5 }, (_, index) => {
    const days = 2 + index;
    return { value: String(days), label: `${days} Days` };
});

export const MINUTES_TO_APPT_OPTIONS = [
    { value: '0', label: '0 Minutes' },
    ...Array.from({ length: 6 }, (_, index) => {
        const minutes = 5 + index * 5;
        return { value: String(minutes), label: `${minutes} Minutes` };
    }),
];

export const REMINDER_CATEGORIES = [
    {
        title: 'Basic Settings',
        icon: 'bi-gear',
        items: [
            { key: 'apptReminder', name: 'Appointment Reminders', description: 'Enable automated appointment reminders for patients', icon: 'bi-calendar-check', type: 'toggle' },
            { key: 'reminderTime', name: 'Reminder Time', description: 'Default time to send daily reminders', icon: 'bi-clock', type: 'time', minHour: 8, maxHour: 18 },
            { key: 'isSameDayReminder', name: 'Same Day Reminders', description: 'Send reminders on the day of appointment', icon: 'bi-calendar-day', type: 'toggle' },
            { key: 'sameDayReminderTime', name: 'Same Day Reminder Time', description: 'Time to send same-day appointment reminders', icon: 'bi-alarm', type: 'time', minHour: 8, maxHour: 11 },
            { key: 'isPrebookedReminder', name: 'Prebooked Reminders', description: 'Enable automated prebooked reminders for patients', icon: 'bi-calendar-plus', type: 'toggle' },
            { key: 'daysPrebookReminder', name: 'Prebook Reminder (Days)', description: 'Days before appointment for prebook reminder', icon: 'bi-123', type: 'select', options: PREBOOK_REMINDER_DAY_OPTIONS },
        ],
    },
    {
        title: 'Reminder Schedule',
        icon: 'bi-card-checklist',
        items: [
            { key: 'isFirstReminder', name: 'First Reminder', description: 'Send initial appointment reminder', icon: 'bi-1-circle', type: 'toggle' },
            { key: 'daysFirstReminder', name: 'First Reminder (Days)', description: 'Days before appointment for first reminder', icon: 'bi-calendar-week', type: 'select', options: FIRST_REMINDER_DAY_OPTIONS },
            { key: 'isSecondReminder', name: 'Second Reminder', description: 'Send follow-up reminder', icon: 'bi-2-circle', type: 'toggle' },
            { key: 'daysSecondReminder', name: 'Second Reminder (Days)', description: 'Days before appointment for second reminder', icon: 'bi-calendar-week', type: 'select', options: SECOND_REMINDER_DAY_OPTIONS },
            { key: 'isLastReminder', name: 'Last Reminder', description: 'Send final reminder before appointment (Call only)', icon: 'bi-3-circle', type: 'toggle' },
            { key: 'minutesToAppt', name: 'Minutes Before Appointment', description: 'Send last reminder by call this many minutes before appointment', icon: 'bi-hourglass-split', type: 'select', options: MINUTES_TO_APPT_OPTIONS },
        ],
    },
    {
        title: 'Communication Channels',
        icon: 'bi-telephone',
        items: [
            { key: 'isEmail', name: 'Email Notifications', description: 'Send reminders and notifications via email', icon: 'bi-envelope', type: 'toggle' },
            { key: 'isSms', name: 'SMS Notifications', description: 'Send reminders via text message', icon: 'bi-phone', type: 'toggle' },
            { key: 'isCall', name: 'Phone Call Notifications', description: 'Send reminders via automated phone calls', icon: 'bi-telephone', type: 'toggle' },
            { key: 'callPreference', name: 'Call Preference', description: 'Preferred phone number for automated calls', icon: 'bi-telephone-forward', type: 'select', options: CALL_PREFERENCE_OPTIONS },
        ],
    },
    {
        title: 'Special Notifications',
        icon: 'bi-gift',
        items: [
            { key: 'isBirthday', name: 'Birthday Messages', description: 'Send birthday greetings to patients', icon: 'bi-cake2', type: 'toggle' },
            { key: 'isWelcome', name: 'Welcome Messages', description: 'Send welcome messages to new patients', icon: 'bi-door-open', type: 'toggle' },
            { key: 'isThankyou', name: 'Thank You Messages', description: 'Send thank you messages after visits', icon: 'bi-heart', type: 'toggle' },
            { key: 'isClReplacement', name: 'Contact Lens Replacement', description: 'Send contact lens replacement messages', icon: 'bi-arrow-repeat', type: 'toggle' },
            { key: 'isClReorder', name: 'Contact Lens Reorder', description: 'Send contact lens reorder messages', icon: 'bi-bag-check', type: 'toggle' },
        ],
    },
    {
        title: 'Summary & Follow-up',
        icon: 'bi-file-text',
        items: [
            { key: 'isApptSummary', name: 'Appointment Summary', description: 'Send appointment summary after visit', icon: 'bi-journal-text', type: 'toggle' },
            { key: 'apptSummary', name: 'Summary Recipient', description: 'Preferred person to receive appointment summary', icon: 'bi-person-check', type: 'select', options: APPT_SUMMARY_OPTIONS },
            { key: 'isClGlSummary', name: 'Contact Lens & Glasses Summary', description: 'Send contact lens and glasses summary after visit', icon: 'bi-eyeglasses', type: 'toggle' },
            { key: 'isFollowup', name: 'Follow-up Reminders', description: 'Send follow-up care reminders', icon: 'bi-arrow-return-right', type: 'toggle' },
            { key: 'isClPickup', name: 'Contact Lens Pickup', description: 'Notify when contact lenses are ready', icon: 'bi-bag', type: 'toggle' },
            { key: 'isGlPickup', name: 'Glasses Pickup', description: 'Notify when glasses are ready', icon: 'bi-eyeglasses', type: 'toggle' },
        ],
    },
];

export const TOGGLE_KEYS = REMINDER_CATEGORIES.flatMap((category) =>
    category.items.filter((item) => item.type === 'toggle').map((item) => item.key)
);

export const CHANNEL_KEYS = ['isEmail', 'isSms', 'isCall'];

export const REMINDER_PAYLOAD_KEYS = [
    'apptReminder',
    'reminderTime',
    'sameDayReminderTime',
    'isPrebookedReminder',
    'isFirstReminder',
    'isSecondReminder',
    'isLastReminder',
    'isSameDayReminder',
    'daysPrebookReminder',
    'daysFirstReminder',
    'daysSecondReminder',
    'minutesToAppt',
    'isClReplacement',
    'isClReorder',
    'isBirthday',
    'isThankyou',
    'isWelcome',
    'isEmail',
    'isSms',
    'isCall',
    'isApptSummary',
    'isClGlSummary',
    'apptSummary',
    'callPreference',
    'isGlPickup',
    'isClPickup',
    'isFollowup',
];

export const getEcpDisplayName = (ecp) => {
    const name = [ecp?.firstName, ecp?.lastName].filter(Boolean).join(' ').trim();
    return name || ecp?.username || 'ECP';
};

export const getDefaultReminderSettings = () =>
    normalizeSettings({
        apptReminder: false,
        reminderTime: '09:00',
        isSameDayReminder: false,
        sameDayReminderTime: '08:00',
        isPrebookedReminder: false,
        daysPrebookReminder: 36,
        isFirstReminder: false,
        daysFirstReminder: 7,
        isSecondReminder: false,
        daysSecondReminder: 2,
        isLastReminder: false,
        minutesToAppt: 5,
        isEmail: false,
        isSms: false,
        isCall: false,
        callPreference: 'DISABLED',
        isBirthday: false,
        isWelcome: false,
        isThankyou: false,
        isClReplacement: false,
        isClReorder: false,
        isApptSummary: false,
        apptSummary: 'ECP',
        isClGlSummary: false,
        isFollowup: false,
        isClPickup: false,
        isGlPickup: false,
    });

export const normalizeSettings = (data = {}) => ({
    ...data,
    reminderTime: normalizeTimeToInterval(data.reminderTime, 60) || '',
    sameDayReminderTime: normalizeTimeToInterval(data.sameDayReminderTime, 60) || '',
    daysPrebookReminder:
        data.daysPrebookReminder !== undefined && data.daysPrebookReminder !== null
            ? String(data.daysPrebookReminder)
            : data.daysPrebookReminder,
    daysFirstReminder:
        data.daysFirstReminder !== undefined && data.daysFirstReminder !== null
            ? String(data.daysFirstReminder)
            : data.daysFirstReminder,
    daysSecondReminder:
        data.daysSecondReminder !== undefined && data.daysSecondReminder !== null
            ? String(data.daysSecondReminder)
            : data.daysSecondReminder,
    minutesToAppt:
        data.minutesToAppt !== undefined && data.minutesToAppt !== null
            ? String(data.minutesToAppt)
            : data.minutesToAppt,
});

export const toApiPayload = (data) => ({
    ...data,
    reminderTime: data.reminderTime ? `${normalizeTimeValue(data.reminderTime)}:00` : data.reminderTime,
    sameDayReminderTime: data.sameDayReminderTime
        ? `${normalizeTimeValue(data.sameDayReminderTime)}:00`
        : data.sameDayReminderTime,
    daysPrebookReminder:
        data.daysPrebookReminder !== undefined && data.daysPrebookReminder !== null && data.daysPrebookReminder !== ''
            ? Number(data.daysPrebookReminder)
            : data.daysPrebookReminder,
    daysFirstReminder:
        data.daysFirstReminder !== undefined && data.daysFirstReminder !== null && data.daysFirstReminder !== ''
            ? Number(data.daysFirstReminder)
            : data.daysFirstReminder,
    daysSecondReminder:
        data.daysSecondReminder !== undefined && data.daysSecondReminder !== null && data.daysSecondReminder !== ''
            ? Number(data.daysSecondReminder)
            : data.daysSecondReminder,
    minutesToAppt:
        data.minutesToAppt !== undefined && data.minutesToAppt !== null && data.minutesToAppt !== ''
            ? Number(data.minutesToAppt)
            : data.minutesToAppt,
});

export const stripToReminderPayload = (data = {}) => {
    const payload = toApiPayload(data);

    return REMINDER_PAYLOAD_KEYS.reduce((acc, key) => {
        if (payload[key] !== undefined && payload[key] !== null) {
            acc[key] = payload[key];
        }

        return acc;
    }, {});
};

const getOptionLabel = (options, value) =>
    options?.find((option) => String(option.value) === String(value))?.label || value || '—';

export const getDisplayValue = (item, values) => {
    const value = values?.[item.key];

    if (item.type === 'time') {
        return value ? formatDisplayTime(value) : '—';
    }

    if (item.type === 'number') {
        return value !== undefined && value !== null ? String(value) : '—';
    }

    if (item.type === 'select') {
        return getOptionLabel(item.options, value);
    }

    return null;
};

export const ReminderSettingControl = ({ item, register, control, errors, fieldIdPrefix }) => {
    const fieldId = `${fieldIdPrefix}-${item.key}`;

    if (item.type === 'toggle') {
        return (
            <div className="form-check form-switch mb-0 reminder-list-item__toggle">
                <input className="form-check-input" type="checkbox" id={fieldId} {...register(item.key)} />
                <label className="form-check-label" htmlFor={fieldId}>
                    Enabled
                </label>
            </div>
        );
    }

    if (item.type === 'time') {
        return (
            <div className="reminder-list-item__control reminder-list-item__control--time">
                <Controller
                    name={item.key}
                    control={control}
                    render={({ field }) => (
                        <EcxTimePicker
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select time"
                            minHour={item.minHour ?? 8}
                            maxHour={item.maxHour ?? 18}
                            intervalMinutes={60}
                        />
                    )}
                />
                {errors?.[item.key] && (
                    <div className="text-danger small mt-1">{errors[item.key].message}</div>
                )}
            </div>
        );
    }

    if (item.type === 'number') {
        return (
            <div className="reminder-list-item__control">
                <input
                    type="number"
                    className={`form-control form-control-sm ${errors?.[item.key] ? 'is-invalid' : ''}`}
                    {...register(item.key, item.validation)}
                />
                {errors?.[item.key] && (
                    <div className="invalid-feedback">{errors[item.key].message}</div>
                )}
            </div>
        );
    }

    return (
        <div className="reminder-list-item__control">
            <select
                className={`form-select form-select-sm ${errors?.[item.key] ? 'is-invalid' : ''}`}
                {...register(item.key)}
            >
                {item.options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            {errors?.[item.key] && (
                <div className="invalid-feedback">{errors[item.key].message}</div>
            )}
        </div>
    );
};

export const ReminderSettingListItem = ({ item, values, isEditing, register, control, errors, fieldIdPrefix }) => {
    const value = values?.[item.key];
    const isToggle = item.type === 'toggle';
    const isEnabled = isToggle ? Boolean(value) : true;
    const displayValue = getDisplayValue(item, values);

    return (
        <div
            className={`clinic-settings-record-item reminder-list-item ${
                isEditing ? 'clinic-settings-record-item--editing' : ''
            } ${isToggle ? (isEnabled ? 'reminder-list-item--enabled' : 'reminder-list-item--disabled') : ''}`}
        >
            <span className="clinic-settings-record-item__avatar">
                <i className={`bi ${item.icon}`}></i>
            </span>
            <div className="clinic-settings-record-item__main">
                <h6 className="clinic-settings-record-item__title">{item.name}</h6>
                <p className="clinic-settings-record-item__meta mb-0">{item.description}</p>
            </div>
            <div className="clinic-settings-record-item__actions reminder-list-item__actions">
                {isEditing ? (
                    <ReminderSettingControl
                        item={item}
                        register={register}
                        control={control}
                        errors={errors}
                        fieldIdPrefix={fieldIdPrefix}
                    />
                ) : isToggle ? (
                    <span className={`clinic-settings-status-pill ${isEnabled ? 'is-open' : 'is-closed'}`}>
                        <i className={`bi ${isEnabled ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}></i>
                        {isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                ) : (
                    <span className="reminder-list-item__value">{displayValue}</span>
                )}
            </div>
        </div>
    );
};

export const ReminderCategoryPanel = ({ category, values, isEditing, register, control, errors, fieldIdPrefix }) => {
    const enabledCount = category.items.filter((item) => item.type === 'toggle' && Boolean(values?.[item.key])).length;
    const toggleCount = category.items.filter((item) => item.type === 'toggle').length;

    return (
        <ClinicInfoPanel
            title={category.title}
            icon={category.icon}
            actions={
                toggleCount > 0 ? (
                    <span className="ecx-page-chip">
                        <i className="bi bi-check-circle"></i>
                        {enabledCount}/{toggleCount} enabled
                    </span>
                ) : null
            }
        >
            <div className="clinic-settings-record-list">
                {category.items.map((item) => (
                    <ReminderSettingListItem
                        key={item.key}
                        item={item}
                        values={values}
                        isEditing={isEditing}
                        register={register}
                        control={control}
                        errors={errors}
                        fieldIdPrefix={fieldIdPrefix}
                    />
                ))}
            </div>
        </ClinicInfoPanel>
    );
};

export const ReminderOverviewStats = ({
    allItemsCount,
    enabledCount,
    activeChannels,
    selectedScope,
    ecpUsers,
    ecpLoading,
    isEditing,
    isSaving,
    onScopeChange,
}) => {
    const showScope = typeof onScopeChange === 'function';

    return (
        <div className={`reminder-overview-stats ${showScope ? 'reminder-overview-stats--with-scope' : ''}`}>
            <div className="reminder-overview-stat">
                <span className="reminder-overview-stat__icon">
                    <i className="bi bi-grid-3x3-gap"></i>
                </span>
                <div>
                    <span className="reminder-overview-stat__label">Total Settings</span>
                    <span className="reminder-overview-stat__value">{allItemsCount}</span>
                </div>
            </div>
            <div className="reminder-overview-stat">
                <span className="reminder-overview-stat__icon is-enabled">
                    <i className="bi bi-check-circle-fill"></i>
                </span>
                <div>
                    <span className="reminder-overview-stat__label">Enabled</span>
                    <span className="reminder-overview-stat__value">{enabledCount}</span>
                </div>
            </div>
            <div className="reminder-overview-stat">
                <span className="reminder-overview-stat__icon">
                    <i className="bi bi-broadcast"></i>
                </span>
                <div>
                    <span className="reminder-overview-stat__label">Active Channels</span>
                    <span className="reminder-overview-stat__value">{activeChannels}</span>
                </div>
            </div>
            {showScope && (
                <div className="reminder-overview-stat reminder-overview-stat--scope">
                    <span className="reminder-overview-stat__icon">
                        <i className="bi bi-person-badge"></i>
                    </span>
                    <div className="reminder-overview-stat__scope">
                        <label className="reminder-overview-stat__label" htmlFor="reminder-scope-select">
                            Settings For
                        </label>
                        <select
                            id="reminder-scope-select"
                            className="form-select form-select-sm reminder-overview-stat__select"
                            value={selectedScope}
                            onChange={(event) => onScopeChange(event.target.value)}
                            disabled={ecpLoading || isEditing || isSaving || !ecpUsers?.length}
                        >
                            {ecpLoading && <option value="">Loading ECPs...</option>}
                            {!ecpLoading && !ecpUsers?.length && <option value="">No ECPs found</option>}
                            {(ecpUsers || []).map((ecp) => (
                                <option key={ecp.id} value={ecp.id}>
                                    {getEcpDisplayName(ecp)}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>
    );
};

export const ReminderSettingsPanels = ({
    values,
    isEditing,
    register,
    control,
    errors,
    fieldIdPrefix,
}) => (
    <div className="reminder-category-panels">
        {REMINDER_CATEGORIES.map((category) => (
            <ReminderCategoryPanel
                key={category.title}
                category={category}
                values={values}
                isEditing={isEditing}
                register={register}
                control={control}
                errors={errors}
                fieldIdPrefix={fieldIdPrefix}
            />
        ))}
    </div>
);
